<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black bg-opacity-50"
    @click="handleClickOutside"
  >
    <div
      class="relative w-full h-auto max-w-5xl p-6 overflow-y-auto bg-white rounded-lg"
      style="max-height: 90vh"
      @click.stop
    >
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-bold text-gray-900">{{ title }}</h2>
        <button
          type="button"
          aria-label="Close"
          @click="closeModal"
          class="text-gray-500 transition hover:text-gray-700"
        >
          <i class="text-xl fa-solid fa-xmark"></i>
        </button>
      </div>

      <form class="space-y-6" @submit.prevent="submit" enctype="multipart/form-data">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <!-- Left: text content -->
          <div class="space-y-4">
            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">Judul</label>
              <input
                type="text"
                v-model="form.title"
                placeholder="Fakultas Ekonomi dan Bisnis"
                class="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">Deskripsi</label>
              <textarea
                v-model="form.description"
                rows="4"
                placeholder="Deskripsi singkat section..."
                class="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              ></textarea>
            </div>

            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">URL Pendaftaran</label>
              <input
                type="text"
                v-model="form.registration_url"
                placeholder="https://spmb.ung.ac.id"
                class="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Label Utama</label>
                <input
                  type="text"
                  v-model="form.mahasiswa_label"
                  placeholder="Mahasiswa"
                  class="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Nilai Utama</label>
                <input
                  type="text"
                  v-model="form.mahasiswa_value"
                  placeholder="100+"
                  class="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          <!-- Right: images + stat boxes -->
          <div class="space-y-4">
            <!-- Images manager (multiple) -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700">
                  Gambar ({{ form.images.length }})
                </label>
                <label
                  class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 rounded-md cursor-pointer bg-indigo-50 hover:bg-indigo-100"
                >
                  <i class="mr-1 fa-solid fa-plus"></i> Tambah gambar
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    class="hidden"
                    @change="onAddImages"
                  />
                </label>
              </div>
              <div class="grid grid-cols-3 gap-2">
                <div
                  v-for="(img, idx) in form.images"
                  :key="img._uid"
                  class="relative overflow-hidden border border-gray-200 rounded-lg group aspect-[4/3] bg-gray-50"
                >
                  <img
                    :src="img.preview"
                    alt="Gambar"
                    class="object-cover w-full h-full"
                  />
                  <button
                    type="button"
                    @click="removeImage(idx)"
                    class="absolute flex items-center justify-center w-6 h-6 text-white transition-opacity rounded-full opacity-0 top-1 right-1 bg-red-600/90 group-hover:opacity-100"
                    title="Hapus gambar"
                  >
                    <i class="text-xs fa-solid fa-xmark"></i>
                  </button>
                </div>
                <p
                  v-if="!form.images.length"
                  class="text-xs text-gray-400 col-span-3"
                >
                  Belum ada gambar. Klik "Tambah gambar".
                </p>
              </div>
            </div>

            <!-- Stat boxes (label + value) -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700">Kotak Statistik</label>
                <button
                  type="button"
                  @click="addStat"
                  class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 rounded-md bg-indigo-50 hover:bg-indigo-100"
                >
                  <i class="mr-1 fa-solid fa-plus"></i> Tambah
                </button>
              </div>
              <div ref="statsListRef" class="space-y-2">
                <div
                  v-for="(stat, i) in form.stats"
                  :key="stat._uid"
                  class="flex items-center gap-2"
                >
                  <!-- Drag handle -->
                  <span
                    class="px-1 text-gray-400 cursor-grab drag-handle shrink-0 hover:text-gray-600"
                    title="Seret untuk mengubah urutan"
                  >
                    <i class="fa-solid fa-grip-vertical"></i>
                  </span>
                  <!-- Icon preview + picker -->
                  <span
                    class="flex items-center justify-center border border-gray-300 rounded-lg shrink-0 w-9 h-9 text-primary bg-gray-50"
                  >
                    <HugeiconsIcon
                      v-if="getJurusanStatIcon(stat.icon)"
                      :icon="getJurusanStatIcon(stat.icon)"
                      :size="18"
                      :stroke-width="1.8"
                    />
                    <i v-else class="text-gray-300 fa-regular fa-image"></i>
                  </span>
                  <select
                    v-model="stat.icon"
                    class="w-32 px-2 py-2 text-sm border border-gray-300 rounded-lg shrink-0 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    title="Pilih ikon"
                  >
                    <option value="">Tanpa ikon</option>
                    <option v-for="opt in iconOptions" :key="opt.key" :value="opt.key">
                      {{ opt.label }}
                    </option>
                  </select>
                  <input
                    type="text"
                    v-model="stat.label"
                    placeholder="Label (mis. Dosen)"
                    class="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <input
                    type="text"
                    v-model="stat.value"
                    placeholder="Nilai"
                    class="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg shrink-0 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div class="flex flex-col shrink-0">
                    <button
                      type="button"
                      @click="moveStat(i, -1)"
                      :disabled="i === 0"
                      class="px-1.5 text-gray-500 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Naikkan"
                    >
                      <i class="text-xs fa-solid fa-chevron-up"></i>
                    </button>
                    <button
                      type="button"
                      @click="moveStat(i, 1)"
                      :disabled="i === form.stats.length - 1"
                      class="px-1.5 text-gray-500 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Turunkan"
                    >
                      <i class="text-xs fa-solid fa-chevron-down"></i>
                    </button>
                  </div>
                  <button
                    type="button"
                    @click="removeStat(i)"
                    class="p-2 text-red-600 rounded-md shrink-0 hover:bg-red-50"
                    title="Hapus"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
                <p v-if="!form.stats.length" class="text-xs text-gray-400">
                  Belum ada kotak statistik. Klik "Tambah".
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tata Letak -->
        <div class="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h3 class="mb-3 text-sm font-semibold text-gray-900">
            <i class="mr-1 fa-solid fa-table-cells-large"></i> Tata Letak
          </h3>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">
                Jumlah kolom kotak statistik (desktop)
              </label>
              <select
                v-model.number="form.stats_columns"
                class="block w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option :value="1">1 kolom</option>
                <option :value="2">2 kolom</option>
                <option :value="3">3 kolom</option>
                <option :value="4">4 kolom</option>
              </select>
            </div>
            <div class="flex items-center">
              <label class="inline-flex items-center gap-2 mt-5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="form.reverse_layout"
                  class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                Tukar posisi (gambar &harr; kotak statistik)
              </label>
            </div>
          </div>

          <div class="mt-4">
            <p class="mb-2 text-sm font-medium text-gray-700">Tampilkan bagian</p>
            <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" v-model="form.show_images" class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                Gambar
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" v-model="form.show_mahasiswa_card" class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                Kartu utama
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" v-model="form.show_stats" class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                Kotak statistik
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" v-model="form.show_register_button" class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                Tombol daftar
              </label>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Tutup
          </button>
          <button
            type="submit"
            :disabled="processing"
            class="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <i class="mr-1 fa-solid fa-floppy-disk"></i>
            {{ processing ? "Menyimpan..." : "Simpan" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, inject, nextTick } from "vue";
import { router } from "@inertiajs/vue3";
import Swal from "sweetalert2";
import Sortable from "sortablejs";
import { HugeiconsIcon } from "@hugeicons/vue";
import { jurusanStatIcons, getJurusanStatIcon } from "@/Components/Web/jurusanStatIcons";

// uid stabil untuk key v-for (agar drag tidak glitch).
let _uidSeq = 0;
const uid = () => `s${Date.now().toString(36)}${++_uidSeq}`;

const route = inject("route");

const props = defineProps({
  isOpen: { type: Boolean, required: true },
  title: { type: String, default: "Edit JurusanStats" },
  jurusanstat: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update:isOpen"]);

const iconOptions = jurusanStatIcons;
const processing = ref(false);
const form = ref({
  title: "",
  description: "",
  registration_url: "",
  mahasiswa_label: "Mahasiswa",
  mahasiswa_value: "100+",
  // Daftar gambar: { _uid, path? (existing), file? (new), preview }
  images: [],
  stats: [],
  // Layout settings
  stats_columns: 3,
  reverse_layout: false,
  show_images: true,
  show_mahasiswa_card: true,
  show_stats: true,
  show_register_button: true,
});

const toUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//.test(path) || path.startsWith("/storage")) return path;
  if (path.startsWith("storage/")) return "/" + path;
  return "/storage/" + path;
};

const hydrate = () => {
  const d = props.jurusanstat?.data || props.jurusanstat || {};
  form.value = {
    title: d.title || "",
    description: d.description || "",
    registration_url: d.registration_url || "",
    mahasiswa_label: d.mahasiswa_label || "Mahasiswa",
    mahasiswa_value: d.mahasiswa_value || "",
    images: Array.isArray(d.images)
      ? d.images
          .filter(Boolean)
          .map((p) => ({ _uid: uid(), path: p, preview: toUrl(p) }))
      : [],
    stats: Array.isArray(d.stats)
      ? d.stats.map((s) => ({
          label: s.label || "",
          value: s.value || "",
          icon: s.icon || "",
          _uid: uid(),
        }))
      : [],
    stats_columns: Number(d.stats_columns) || 3,
    reverse_layout: d.reverse_layout ?? false,
    show_images: d.show_images ?? true,
    show_mahasiswa_card: d.show_mahasiswa_card ?? true,
    show_stats: d.show_stats ?? true,
    show_register_button: d.show_register_button ?? true,
  };
};

// Re-fill the form & (re)init drag-and-drop whenever the modal is opened.
watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      hydrate();
      await nextTick();
      initSortable();
    } else {
      destroySortable();
    }
  },
  { immediate: true }
);

const closeModal = () => emit("update:isOpen", false);
const handleClickOutside = (e) => {
  if (e.target === e.currentTarget) closeModal();
};

const addStat = () =>
  form.value.stats.push({ label: "", value: "", icon: "", _uid: uid() });
const removeStat = (i) => form.value.stats.splice(i, 1);
const moveStat = (i, dir) => {
  const j = i + dir;
  const list = form.value.stats;
  if (j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
};

// ===== Drag & drop (SortableJS) =====
const statsListRef = ref(null);
let sortable = null;

const initSortable = () => {
  if (!statsListRef.value || sortable) return;
  sortable = Sortable.create(statsListRef.value, {
    handle: ".drag-handle",
    animation: 150,
    ghostClass: "opacity-50",
    onEnd: (evt) => {
      const { oldIndex, newIndex } = evt;
      if (oldIndex === newIndex || oldIndex == null || newIndex == null) return;
      const arr = form.value.stats;
      const [moved] = arr.splice(oldIndex, 1);
      arr.splice(newIndex, 0, moved);
    },
  });
};

const destroySortable = () => {
  if (sortable) {
    sortable.destroy();
    sortable = null;
  }
};

const onAddImages = (event) => {
  const files = Array.from(event.target.files || []);
  let rejected = false;
  for (const file of files) {
    if (file.type.startsWith("image/")) {
      form.value.images.push({
        _uid: uid(),
        file,
        preview: URL.createObjectURL(file),
      });
    } else {
      rejected = true;
    }
  }
  // reset input agar file yang sama bisa dipilih lagi
  event.target.value = "";
  if (rejected) {
    Swal.fire({ title: "Error!", text: "Hanya file gambar yang diperbolehkan.", icon: "error" });
  }
};

const removeImage = (idx) => {
  const img = form.value.images[idx];
  if (img?.file && img.preview) URL.revokeObjectURL(img.preview);
  form.value.images.splice(idx, 1);
};

const submit = () => {
  processing.value = true;
  const fd = new FormData();
  fd.append("title", form.value.title || "");
  fd.append("description", form.value.description || "");
  fd.append("registration_url", form.value.registration_url || "");
  fd.append("mahasiswa_label", form.value.mahasiswa_label || "");
  fd.append("mahasiswa_value", form.value.mahasiswa_value || "");
  fd.append("stats", JSON.stringify(form.value.stats || []));
  // Layout settings
  fd.append("stats_columns", String(form.value.stats_columns || 3));
  fd.append("reverse_layout", form.value.reverse_layout ? "1" : "0");
  fd.append("show_images", form.value.show_images ? "1" : "0");
  fd.append("show_mahasiswa_card", form.value.show_mahasiswa_card ? "1" : "0");
  fd.append("show_stats", form.value.show_stats ? "1" : "0");
  fd.append("show_register_button", form.value.show_register_button ? "1" : "0");
  // Daftar gambar: kirim urutan token + file baru sesuai urutan.
  const order = [];
  for (const img of form.value.images) {
    if (img.file instanceof File) {
      order.push("__NEW__");
      fd.append("new_images[]", img.file);
    } else if (img.path) {
      order.push(img.path);
    }
  }
  fd.append("images_order", JSON.stringify(order));

  router.post(route("jurusanstats.update"), fd, {
    forceFormData: true,
    preserveScroll: true,
    onSuccess: () => {
      closeModal();
    },
    onError: () => {
      Swal.fire({
        title: "Gagal!",
        text: "Terjadi kesalahan saat menyimpan.",
        icon: "error",
        confirmButtonText: "OK",
      });
    },
    onFinish: () => {
      processing.value = false;
    },
  });
};
</script>
